import React, { useEffect } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Lightweight fullscreen image viewer used by product cards & detail page.
// - Tap anywhere or X to close
// - `esc` closes on desktop
// - Body scroll is locked while open
// - iPhone-friendly: fills the viewport, uses object-contain so nothing is cropped
export default function ImageLightbox({ open, src, alt = "", onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-md flex items-center justify-center touch-manipulation"
          onClick={onClose}
          data-testid="image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={alt || "Product image"}
        >
          <button
            data-testid="image-lightbox-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 md:top-6 md:right-6 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 border border-white/25 text-white hover:bg-white/20 active:scale-95 transition-transform"
          >
            <X size={22} />
          </button>
          <motion.img
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[96vw] max-h-[92vh] object-contain drop-shadow-[0_0_60px_rgba(147,51,234,0.4)] select-none"
            draggable={false}
          />
          <p className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-white/60 font-mono-alt">
            Tap anywhere to close
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
